import { BrowserModule } from "@angular/platform-browser";
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, APP_INITIALIZER, ErrorHandler, inject } from "@angular/core";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { LocationStrategy, PathLocationStrategy } from "@angular/common";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { RouterModule, Routes } from "@angular/router";

import { AppComponent } from "./app.component";
import { LoadingScreenComponent } from "./loading-screen/loading-screen.component";

// DI
import { AuthInterceptor } from "./service/auth.interceptor";
import { AuthService } from "./service/auth.service";
import { MailBoxService } from "./service/mailbox.service";
import { SoApiService } from "./service/soapi.service";
import { ConfigurationService } from "./service/configuration.service";
import { ResourceService } from "./service/resource.service";
import { LoadingScreenService } from "./service/loading-screen.service";
import { ComposeService } from "./service/compose.service";
import { MonitoringService } from "./service/monitoring.service";
import { AttachmentService } from "./service/attachment.service";
import { ErrorService } from "./service/error.service";
import { SessionStorageService } from "./service/session-storage.service";
import { CancelhttpbetweenroutesInterceptor } from "./service/cancelhttpbetweenroutes.interceptor";
import { HttpcancelService } from "./service/httpcancel.service";

// For routing!
import { OfficeJSGuardService } from "./service/officejs-guard.service";
import { AuthGuardService } from "./service/auth-guard.service";
import { LandingScreenResolver } from "./service/landing-screen.resolver";
import { UnsafeFileService } from "./service/unsafe-files.service";
import { LoadedContextService } from "./service/loadedContext.service";
import { OutlookService } from "./service/outlook/outlook.service";
import { SoLoginDialog } from "./utilities/soLoginDialog";
import { SoAuthenticator } from "./utilities/soAuthenticator";
import { PreferencesResolver } from "./service/preferences.resolver";
import { BladeService } from "./service/blade.service";
import { AppointmentSharedService } from "./service/appointment.shared.service";
import { GraphService } from "./service/graph/graph.service";
import { ErrorBannerComponent } from "./error-banner/error-banner.component";
import { UIErrorFactory } from "./service/ui-error.factory";
import { FeatureAnnouncementService } from "./service/feature-announcement.service";

const ensureEnrolled = (authGuardService = inject(AuthGuardService)) => authGuardService.ensureEnrolled();
const canAccessOffice365 = (officeJSGuardService = inject(OfficeJSGuardService)) => officeJSGuardService.canAccessOffice365();

const routes: Routes = [
  { path: "", redirectTo: "inbox/action", pathMatch: "full" },
  {
    path: "auth-callback",
    loadComponent: () => import("./auth-callback/auth-callback.component").then(c => c.AuthCallbackComponent),
  },
  {
    path: "sign-in",
    loadComponent: () => import("./sign-in/sign-in.component").then(c => c.SignInComponent),
    title: "Sign In"
  },
  {
    path: "sign-in/onsite-configuration",
    loadComponent: () => import("./sign-in/onsite-configuration/onsite-configuration.component").then(c => c.OnsiteConfigurationComponent),
    title: "Onsite Configuration"
  },
  {
    path: "read/action", redirectTo: "inbox/action", pathMatch: "prefix",
    title: "Action"
  },
  {
    path: "inbox",
    loadComponent: () => import("./inbox/inbox.component").then(c => c.InboxComponent),
    canActivate: [() => canAccessOffice365(), () => inject(AuthGuardService).ensureEnrolled()],
    runGuardsAndResolvers: "always",
    resolve: { mailInfo: LandingScreenResolver, prefs: PreferencesResolver },
    children: [
      {
        path: "action",
        loadComponent: () => import("./inbox/action/action.component").then(c => c.ActionComponent),
        title: "Inbox/Action"
      },
      {
        path: "people",
        loadComponent: () => import("./inbox/people/people.component").then(c => c.PeopleComponent),
        title: "Inbox/People"
      }
    ]
  },
  {
    path: "compose/compose-archive",
    loadComponent: () => import("./compose/compose-archive/compose-archive.component").then(c => c.ComposeArchiveComponent),
    runGuardsAndResolvers: "always",
    resolve: { mailInfo: LandingScreenResolver, prefs: PreferencesResolver },
    canActivate: [() => canAccessOffice365(),() => ensureEnrolled()],
    title: "Compose Archive email"
  },
  {
    path: "appointment",
    loadComponent: () => import("./appointment/appointment.component")
      .then(c => c.AppointmentComponent),
    runGuardsAndResolvers: "always",
    resolve: { mailInfo: LandingScreenResolver, prefs: PreferencesResolver },
    canActivate: [() => canAccessOffice365(), () => inject(AuthGuardService).ensureEnrolled()],
    title: "Appointment"
  },
  {
    path: "compose/compose-appointment",
    loadComponent: () => import("./compose/compose-appointment/compose-appointment.component")
      .then(c => c.ComposeAppointmentComponent),
    runGuardsAndResolvers: "always",
    resolve: { mailInfo: LandingScreenResolver, prefs: PreferencesResolver },
    canActivate: [() => canAccessOffice365(), () => inject(AuthGuardService).ensureEnrolled()],
    title: "Compose Appointment"
  },
  {
    path: "compose/compose-appointment/add-attendee",
    loadComponent: () => import("./compose/compose-appointment/add-attendee/add-attendee.component").then(c => c.AddAttendeeComponent),
    canActivate: [() => ensureEnrolled()],
    title: "Compose Appointment/Add Attendee"
  },
  {
    path: "compose/compose-add-recipient",
    loadComponent: () => import("./compose/compose-add-recipient/compose-add-recipient.component").then(c => c.ComposeAddRecipientComponent),
    canActivate: [() => ensureEnrolled()],
    title: "Compose Email/Add Recipient"
  },
  {
    path: "contact/select-contact",
    loadComponent: () => import("./contact/select-contact/select-contact.component").then(c => c.SelectContactComponent),
    canActivate: [() => ensureEnrolled()],
    title: "Select Contact"
  },
  {
    path: "contact/save-contact",
    loadComponent: () => import("./contact/save-contact/save-contact.component").then(c => c.SaveContactComponent),
    canActivate: [() => ensureEnrolled()],
    title: "Save Contact"
  },
  {
    path: "contact/add-email-or-contact",
    loadComponent: () => import("./contact/add-email-or-contact/add-email-or-contact.component").then(c => c.AddEMailOrContactComponent),
    canActivate: [() => ensureEnrolled()],
    title: "Add Email or Contact"
  },
  {
    path: "archive",
    loadComponent: () => import("./archive/archive.component").then(c => c.ArchiveComponent),
    canActivate: [() => ensureEnrolled()],
    title: "Archive Email"
  },
  {
    path: "activity/activity-create",
    loadComponent: () => import("./activity/activity-create/activity-create.component").then(c => c.ActivityCreateComponent),
    canActivate: [() => ensureEnrolled()],
    title: "Create Activity"
  },
  {
    path: "task/task-create",
    loadComponent: () => import("./task/task-create/task-create.component").then(c => c.TaskCreateComponent),
    canActivate: [() => ensureEnrolled()],
    title: "Create Task"
  },
  {
    path: "request/request-create",
    loadComponent: () => import("./request/request-create/request-create.component").then(c => c.RequestCreateComponent),
    canActivate: [() => ensureEnrolled()],
    title: "Create Request"
  },
  {
    path: "request/request-message-create",
    loadComponent: () => import("./request/request-message-create/request-message-create.component").then(c => c.RequestMessageCreateComponent),
    canActivate: [() => ensureEnrolled()],
    title: "Create Request Message"
  },
  {
    path: "sale/sale-create",
    loadComponent: () => import("./sale/sale-create/sale-create.component").then(c => c.SaleCreateComponent),
    canActivate: [() => ensureEnrolled()],
    title: "Create Sale"
  },
  {
    path: "attachment/attachment-select",
    loadComponent: () => import("./attachment/attachment-select/attachment-select.component").then(c => c.AttachmentSelectComponent),
    canActivate: [() => ensureEnrolled()],
    title: "Select Attachment"
  },
  {
    path: "attachment/attachment-save",
    loadComponent: () => import("./attachment/attachment-save/attachment-save.component").then(c => c.AttachmentSaveComponent),
    canActivate: [() => ensureEnrolled()],
    title: "Save Attachment"
  },
  {
    path: "summary",
    loadComponent: () => import("./summary/summary.component").then(c => c.SummaryComponent),
    canActivate: [() => ensureEnrolled()],
    title: "Summary"
  },
  {
    path: "many-contacts",
    loadComponent: () => import("./many-contacts/many-contacts.component").then(c => c.ManyContactsComponent),
    canActivate: [() => ensureEnrolled()],
    title: "Many Contacts"
  },
  {
    path: "settings",
    loadComponent: () => import("./settings/settings.component").then(c => c.SettingsComponent),
    canActivate: [() => ensureEnrolled()],
    title: "Settings"
  },
  {
    path: "authorization-info",
    loadComponent: () => import("./utilities/authorization-info/authorization-info.component").then(c => c.AuthorizationInfoComponent),
    title: "Authorization Info"
  },
  {
    path: "popup-blocked",
    loadComponent: () => import("./utilities/popup-blocked/popup-blocked.component").then(c => c.PopupBlockedComponent),
    title: "Popup Blocked"
  },
  {
    path: "authorization-in-progress",
    loadComponent: () => import("./utilities/authorization-in-progress/authorization-in-progress.component").then(c => c.AuthorizationInProgressComponent),
    title: "Authorization In Progress"
  },
  {
    path: "error",
    loadComponent: () => import("./error/error.component").then(c => c.ErrorComponent),
    title: "Error",
    children: [
      {
        path: "general",
        loadComponent: () => import("./error/error-general/error-general.component").then(c => c.ErrorGeneralComponent),
        title: "General Error"
      },
      {
        path: "not-subscriber",
        loadComponent: () => import("./error/error-not-office-subscriber/error-not-office-subscriber.component").then(c => c.ErrorNotOfficeSubscriberComponent),
        title: "Not Office Subscriber Error"
      },
      {
        path: "not-office365-client",
        loadComponent: () => import("./error/error-not-office365-client/error-not-office365-client.component").then(c => c.ErrorNotOffice365ClientComponent),
        title: "Not Office365 Client Error"
      }
    ]
  }
];

@NgModule({
    declarations: [
        AppComponent,
    ],
    exports: [],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent],
    imports: [
        RouterModule.forRoot(routes, { onSameUrlNavigation: "reload" }),
        BrowserModule,
        BrowserAnimationsModule,
        LoadingScreenComponent,
        ErrorBannerComponent],
        providers: [
        { provide: LocationStrategy, useClass: PathLocationStrategy },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        {
            provide: ErrorHandler,
            useClass: ErrorService
        },
        OutlookService,
        GraphService,
        AuthService,
        AuthGuardService,
        OfficeJSGuardService,
        SoLoginDialog,
        SoAuthenticator,
        MailBoxService,
        ComposeService,
        SoApiService,
        AttachmentService,
        LoadingScreenService,
        ConfigurationService,
        ResourceService,
        MonitoringService,
        BladeService,
        AppointmentSharedService,
        HttpcancelService,
        SessionStorageService,
        UnsafeFileService,
        {
            provide: APP_INITIALIZER,
            useFactory: (configSvc: ConfigurationService) => () => configSvc.loadConfiguration(),
            deps: [ConfigurationService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (resSvc: ResourceService) => () => resSvc.loadLocalizeResources(),
            deps: [ResourceService],
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CancelhttpbetweenroutesInterceptor,
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => new LoadedContextService(),
            deps: [LoadedContextService],
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (svc: FeatureAnnouncementService) => () => svc.loadAnnouncements().catch(() => {}),
            deps: [FeatureAnnouncementService],
            multi: true,
        },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }
